import { authenticatedFetch } from "./http";
import type { StockRemediation } from "./inventory-count";
import type { AllocationSimulation, ReceiptStockResult, StockAllocation, StockItemSummary, StockLocationSummary, StockMovement, StockUnit, StockWarehouseSummary } from "./models";

export function loadStockByItem(): Promise<StockItemSummary[]> {
  return authenticatedFetch<StockItemSummary[]>("/api/v1/inventory/stock/items");
}

export function loadStockByWarehouse(): Promise<StockWarehouseSummary[]> {
  return authenticatedFetch<StockWarehouseSummary[]>(
    "/api/v1/inventory/stock/warehouses"
  );
}

export function loadStockByLocation(): Promise<StockLocationSummary[]> {
  return authenticatedFetch<StockLocationSummary[]>(
    "/api/v1/inventory/stock/locations"
  );
}

export function loadStockUnitsByScope(
  warehouseId?: string,
  locationType?: "STORAGE_BIN" | "PROCESS_POINT",
  locationId?: string
): Promise<StockUnit[]> {
  const params = new URLSearchParams();
  if (warehouseId) params.set("warehouseId", warehouseId);
  if (locationType && locationId) {
    params.set("locationType", locationType);
    params.set("locationId", locationId);
  }
  return authenticatedFetch<StockUnit[]>(
    `/api/v1/inventory/stock/units${params.size ? `?${params}` : ""}`
  );
}

export function loadAllStockUnits(): Promise<StockUnit[]> {
  return loadStockUnitsByScope();
}

export function loadStockRemediations(): Promise<StockRemediation[]> {
  return authenticatedFetch<StockRemediation[]>(
    "/api/v1/inventory/stock/remediations"
  );
}

export function loadStockUnits(itemId: string): Promise<StockUnit[]> {
  return authenticatedFetch<StockUnit[]>(
    `/api/v1/inventory/stock/items/${itemId}/units`
  );
}

export function loadStockMovements(filters: {
  inventoryUnitId?: string;
  itemId?: string;
  warehouseId?: string;
  locationType?: "STORAGE_BIN" | "PROCESS_POINT";
  locationId?: string;
} = {}): Promise<StockMovement[]> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const query = params.size > 0 ? `?${params}` : "";
  return authenticatedFetch<StockMovement[]>(
    `/api/v1/inventory/stock/movements${query}`
  );
}

export function correctStockRotationDate(
  inventoryUnitId: string,
  rotationDate: string,
  reason: string,
  version: number
): Promise<StockUnit> {
  return authenticatedFetch<StockUnit>(
    `/api/v1/inventory/stock/units/${inventoryUnitId}/rotation-date`,
    {
      method: "PATCH",
      body: JSON.stringify({ rotationDate, reason, version })
    }
  );
}

export function correctStockSerialNumber(
  inventoryUnitId: string,
  serialNumber: string,
  reason: string,
  version: number
): Promise<StockUnit> {
  return authenticatedFetch<StockUnit>(
    `/api/v1/inventory/stock/units/${inventoryUnitId}/serial-number`,
    {
      method: "PATCH",
      body: JSON.stringify({ serialNumber, reason, version })
    }
  );
}

export function correctStockLotTraceability(
  inventoryUnitId: string,
  lotNumber: string,
  expirationDate: string | null,
  reason: string,
  version: number
): Promise<StockUnit> {
  return authenticatedFetch<StockUnit>(
    `/api/v1/inventory/stock/units/${inventoryUnitId}/lot-traceability`,
    {
      method: "PATCH",
      body: JSON.stringify({ lotNumber, expirationDate, reason, version })
    }
  );
}

export function generateReceiptStock(
  receiptId: string, request: Record<string, unknown>
): Promise<ReceiptStockResult> {
  return authenticatedFetch<ReceiptStockResult>(
    `/api/v1/inventory/receipts/${receiptId}/generate`,
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function loadStockAllocations(): Promise<StockAllocation[]> {
  return authenticatedFetch<StockAllocation[]>("/api/v1/allocations");
}

export function simulateStockAllocation(
  request: Record<string, unknown>
): Promise<AllocationSimulation> {
  return authenticatedFetch<AllocationSimulation>(
    "/api/v1/allocations/simulate",
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function createStockAllocation(
  request: Record<string, unknown>
): Promise<StockAllocation> {
  return authenticatedFetch<StockAllocation>("/api/v1/allocations", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function releaseStockAllocation(
  allocationId: string, reason: string, version: number
): Promise<StockAllocation> {
  return authenticatedFetch<StockAllocation>(
    `/api/v1/allocations/${allocationId}/release`,
    { method: "POST", body: JSON.stringify({ reason, version }) }
  );
}
